import { Children, useId, type FunctionComponent } from "react";
import classNames from "classnames";
import { Sentiment } from "@edgetier/watchtower-types";
import type { IFacePathsProps, IGroupProps, IProps, ISvgProps } from "./sentiment-icon.types";
import { SENTIMENT_FACE_PATHS } from "./sentiment-icon.constants";
import { Center } from "../center";
import { Group } from "../group";
import styles from "./sentiment-icon.module.scss";

/**
 * The face of a sentiment icon, drawn on its circle.
 * @param props.sentiment - The sentiment whose face to draw.
 * @returns The SVG paths of the sentiment's face.
 */
const SentimentFacePaths: FunctionComponent<IFacePathsProps> = ({ sentiment }) => (
    <>
        {SENTIMENT_FACE_PATHS[sentiment].map(({ d, isEvenOdd }) => (
            <path
                key={d}
                d={d}
                fillRule={isEvenOdd ? "evenodd" : undefined}
                clipRule={isEvenOdd ? "evenodd" : undefined}
                fill="var(--path-fill)"
            />
        ))}
    </>
);

const Frustration: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_423_8390_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["frustration"], className)}
            data-testid="frustration-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <SentimentFacePaths sentiment={Sentiment.Frustration} />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Empathy: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_423_7578_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["empathy"], className)}
            data-testid="empathy-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <SentimentFacePaths sentiment={Sentiment.Empathy} />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Confusion: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_423_7676_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["confusion"], className)}
            data-testid="confusion-icon"
            data-active={isActive}
            {...svgProps}
        >
            <ellipse cx="9" cy="8.99989" rx="9" ry="8.99843" fill={`url(#${gradientId})`} />
            <SentimentFacePaths sentiment={Sentiment.Confusion} />
            <defs>
                <linearGradient
                    id={gradientId}
                    x1="9"
                    y1="0.00146484"
                    x2="9"
                    y2="17.9983"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Praise: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_762_9361_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["praise"], className)}
            data-testid="praise-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <SentimentFacePaths sentiment={Sentiment.Praise} />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};
const Gratitude: FunctionComponent<ISvgProps> = ({ className, svgProps, isActive = true }) => {
    const id = useId();
    const gradientId = `paint0_linear_762_9334_${id}`;
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            className={classNames(styles["gratitude"], className)}
            data-testid="gratitude-icon"
            data-active={isActive}
            {...svgProps}
        >
            <circle cx="9" cy="9" r="9" fill={`url(#${gradientId})`} />
            <SentimentFacePaths sentiment={Sentiment.Gratitude} />
            <defs>
                <linearGradient id={gradientId} x1="9" y1="0" x2="9" y2="18" gradientUnits="userSpaceOnUse">
                    <stop stopColor="var(--gradient-stop)" />
                    <stop offset="1" stopColor="var(--gradient-stop)" />
                </linearGradient>
            </defs>
        </svg>
    );
};

/**
 * Sentiment icon component for displaying sentiment indicators (frustration, empathy, confusion, praise, gratitude).
 * @param {string} props.sentiment - The sentiment type to display.
 * @param {string} props.className - Additional CSS class names.
 * @param {object} props.svgProps - Additional props to pass to the SVG element.
 * @returns {JSX.Element | null} The SentimentIcon component or null if sentiment is invalid.
 */
const SentimentIcon = (props: IProps) => {
    switch (props.sentiment) {
        case Sentiment.Frustration:
            return <Frustration {...props} />;
        case Sentiment.Confusion:
            return <Confusion {...props} />;
        case Sentiment.Gratitude:
            return <Gratitude {...props} />;
        case Sentiment.Praise:
            return <Praise {...props} />;
        case Sentiment.Empathy:
            return <Empathy {...props} />;
        default:
            return null;
    }
};

/**
 * Group component for displaying multiple sentiment icons together with overlapping effect.
 * @param {string} props.className - Additional CSS class names.
 * @param {React.ReactNode} props.children - SentimentIcon components to group together.
 * @param {string} props.iconBorderColor - Background color for the icon borders.
 * @returns {JSX.Element} The SentimentIconGroup component.
 */
const SentimentIconGroup = ({ className, children, iconBorderColor, ...otherProps }: IGroupProps) => {
    return (
        <Group className={classNames(styles["group"], className)} {...otherProps}>
            {Children.map(children, (child, index) => (
                <Center
                    className={styles["iconWrapper"]}
                    bdrs="xl"
                    bg={iconBorderColor}
                    left={index * -5}
                    style={{ zIndex: index + 1 }}
                >
                    {child}
                </Center>
            ))}
        </Group>
    );
};

SentimentIcon.Frustration = Frustration;
SentimentIcon.Empathy = Empathy;
SentimentIcon.Confusion = Confusion;
SentimentIcon.Praise = Praise;
SentimentIcon.Gratitude = Gratitude;
SentimentIcon.Group = SentimentIconGroup;

export { SentimentIcon };
